import api from './api';

// Profile API Service
const profileApi = {
  // Fetch complete profile data
  getProfile: async () => {
    try {
      const response = await api.get('/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching profile:', error);
      throw error;
    }
  },

  // Update Franchise Profile
  updateFranchiseProfile: async (data) => {
    try {
      const response = await api.put('/profile/franchise', data);
      return response.data;
    } catch (error) {
      console.error('Error updating franchise profile:', error);
      throw error;
    }
  },

  // Update Owner Details
  updateOwnerDetails: async (data) => {
    try {
      const response = await api.put('/profile/owner', data);
      return response.data;
    } catch (error) {
      console.error('Error updating owner details:', error);
      throw error;
    }
  },

  // Upload KYC Document
  uploadKycDocument: async (documentType, documentField, file) => {
    try {
      const formData = new FormData();
      formData.append('documentType', documentType);
      formData.append('documentField', documentField);
      formData.append('file', file);

      const response = await api.post('/profile/kyc/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading KYC document:', error);
      throw error;
    }
  },

  // Verify KYC Document
  verifyKycDocument: async (documentType, documentNumber) => {
    try {
      const response = await api.post('/profile/kyc/verify', {
        documentType,
        documentNumber
      });
      return response.data;
    } catch (error) {
      console.error('Error verifying KYC document:', error);
      throw error;
    }
  },

  // Bank Account Management
  addBankAccount: async (accountData) => {
    try {
      const response = await api.post('/profile/bank-accounts', accountData);
      return response.data;
    } catch (error) {
      console.error('Error adding bank account:', error);
      throw error;
    }
  },

  updateBankAccount: async (accountId, accountData) => {
    try {
      const response = await api.put(`/profile/bank-accounts/${accountId}`, accountData);
      return response.data;
    } catch (error) {
      console.error('Error updating bank account:', error);
      throw error;
    }
  },

  deleteBankAccount: async (accountId) => {
    try {
      const response = await api.delete(`/profile/bank-accounts/${accountId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting bank account:', error);
      throw error;
    }
  },

  setPrimaryBankAccount: async (accountId) => {
    try {
      const response = await api.put(`/profile/bank-accounts/${accountId}/primary`);
      return response.data;
    } catch (error) {
      console.error('Error setting primary bank account:', error);
      throw error;
    }
  }
};

// Cache management utilities
export const profileCache = {
  // Cache keys
  KEYS: {
    PROFILE: 'profile_data',
    TIMESTAMP: 'profile_timestamp'
  },

  // Cache duration (5 minutes)
  CACHE_DURATION: 5 * 60 * 1000,

  // Get cached data
  get: () => {
    try {
      const timestamp = localStorage.getItem(profileCache.KEYS.TIMESTAMP);
      const data = localStorage.getItem(profileCache.KEYS.PROFILE);

      if (!timestamp || !data) return null;

      const age = Date.now() - parseInt(timestamp);
      if (age > profileCache.CACHE_DURATION) {
        profileCache.clear();
        return null;
      }

      return JSON.parse(data);
    } catch (error) {
      console.error('Error reading cache:', error);
      return null;
    }
  },

  // Set cache data
  set: (data) => {
    try {
      localStorage.setItem(profileCache.KEYS.PROFILE, JSON.stringify(data));
      localStorage.setItem(profileCache.KEYS.TIMESTAMP, Date.now().toString());
    } catch (error) {
      console.error('Error setting cache:', error);
    }
  },

  // Clear cache
  clear: () => {
    localStorage.removeItem(profileCache.KEYS.PROFILE);
    localStorage.removeItem(profileCache.KEYS.TIMESTAMP);
  }
};

// Profile API with caching
export const cachedProfileApi = {
  getProfile: async (forceRefresh = false) => {
    // Check cache first
    if (!forceRefresh) {
      const cached = profileCache.get();
      if (cached) {
        console.log('Using cached profile data');
        return { data: cached, fromCache: true };
      }
    }

    // Fetch from API
    console.log('Fetching fresh profile data');
    const response = await profileApi.getProfile();

    // Cache the response
    if (response.status && response.data) {
      profileCache.set(response.data);
    }

    return { ...response, fromCache: false };
  },

  // Clear cache on updates
  clearCache: () => {
    profileCache.clear();
  }
};

export default profileApi;